"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";

export interface ParsedAddress {
  line1: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

interface Suggestion extends ParsedAddress {
  id: string;
  full: string;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (line1: string) => void;
  onSelect: (address: ParsedAddress) => void;
  placeholder?: string;
  /** ISO country code to bias results, e.g. "US". */
  country?: string;
  autoFocus?: boolean;
}

const MAPBOX_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_TOKEN;

function parseFeature(feature: {
  id: string;
  properties?: Record<string, unknown>;
}): Suggestion {
  const props = (feature.properties ?? {}) as Record<string, unknown>;
  const context = (props.context ?? {}) as Record<
    string,
    { name?: string; region_code?: string; country_code?: string }
  >;

  return {
    id: feature.id,
    line1: (props.name as string) ?? (props.full_address as string) ?? "",
    full: (props.full_address as string) ?? (props.name as string) ?? "",
    city: context.place?.name ?? "",
    state: context.region?.region_code ?? context.region?.name ?? "",
    postalCode: context.postcode?.name ?? "",
    country: (context.country?.country_code ?? "").toUpperCase(),
  };
}

export function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder,
  country,
  autoFocus,
}: AddressAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const justSelectedRef = useRef(false);
  const inputFocusedRef = useRef(false);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    if (!inputFocusedRef.current) {
      return;
    }
    const trimmed = value.trim();
    const hasLetter = /[a-zA-Z]/.test(trimmed);
    const hasNumber = /\d/.test(trimmed);
    if (!MAPBOX_TOKEN || trimmed.length < 2 || !hasLetter || !hasNumber) {
      setSuggestions([]);
      setOpen(false);
      return;
    }

    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          q: value,
          autocomplete: "true",
          types: "address",
          limit: "5",
          access_token: MAPBOX_TOKEN,
        });
        if (country) params.set("country", country);
        const res = await fetch(
          `https://api.mapbox.com/search/geocode/v6/forward?${params.toString()}`,
          { signal: controller.signal },
        );
        if (!res.ok) return;
        const data = (await res.json()) as {
          features?: { id: string; properties?: Record<string, unknown> }[];
        };
        const parsed = (data.features ?? [])
          .map(parseFeature)
          .filter((s) => s.line1);
        setSuggestions(parsed);
        setHighlighted(-1);
        setOpen(parsed.length > 0);
      } catch {
        // aborted or network error — ignore
      }
    }, 80);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [value, country]);

  const choose = useCallback(
    (s: Suggestion) => {
      justSelectedRef.current = true;
      onSelect({
        line1: s.line1,
        city: s.city,
        state: s.state,
        postalCode: s.postalCode,
        country: s.country || country || "US",
      });
      setOpen(false);
      setSuggestions([]);
    },
    [onSelect, country],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!open || suggestions.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlighted((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : 0,
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlighted((prev) =>
          prev > 0 ? prev - 1 : suggestions.length - 1,
        );
      } else if (e.key === "Enter" && highlighted >= 0) {
        e.preventDefault();
        choose(suggestions[highlighted]);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    },
    [open, suggestions, highlighted, choose],
  );

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Icon
          name="IconMapPin"
          size={15}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-contrast/40 pointer-events-none"
        />
        <Input
          type="text"
          variant="default"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => {
            inputFocusedRef.current = true;
            if (suggestions.length > 0) setOpen(true);
          }}
          onBlur={() => {
            inputFocusedRef.current = false;
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoFocus={autoFocus}
          autoComplete="off"
          className="pl-9"
        />
      </div>

      <AnimatePresence>
        {open && suggestions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
            style={{ transformOrigin: "top" }}
            className="absolute z-50 top-full mt-1.5 inset-x-0 rounded-lg bg-surface-elevated shadow-[var(--modal-shadow)] overflow-hidden"
          >
            <div className="p-1">
              {suggestions.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choose(s);
                  }}
                  onMouseEnter={() => setHighlighted(i)}
                  className={cn(
                    "w-full flex items-center gap-2 px-2 h-7 rounded-md text-left transition-colors cursor-pointer",
                    i === highlighted ? "bg-contrast/6" : "hover:bg-contrast/4",
                  )}
                >
                  <Icon
                    name="IconMapPin"
                    size={13}
                    className="text-contrast/40 shrink-0"
                  />
                  <span className="text-[13px] text-heading truncate">
                    {s.full}
                  </span>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 border-t border-contrast/6">
              <img
                src="/favicon.svg"
                alt=""
                className="shrink-0 h-[11px] w-auto grayscale opacity-40"
              />
              <span className="text-[11px] font-medium text-contrast/40 truncate">
                Address suggestions for your convenience
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
